import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import MaterialService from "src/Services/API/material.service";

const Archive = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState({});
  const [typesByCategory, setTypesByCategory] = useState({});
  const [typesLoading, setTypesLoading] = useState({});

  useEffect(() => {
    getArchived();
  }, []);

  const getArchived = async () => {
    setIsLoading(true);
    try {
      const [categoryResponse, typesResponse] = await Promise.all([MaterialService.get(), MaterialService.getTypes()]);
      const allCategories = categoryResponse.data || [];
      const allTypes = typesResponse.data || [];

      const archivedCategoryLookup = allCategories.reduce((acc, category) => {
        acc[category.id] = category.isActive === false;
        return acc;
      }, {});

      const archivedTypesByCategory = allTypes.reduce((acc, type) => {
        const categoryId = type.categoryId || type.category?.id;
        if (!categoryId) return acc;
        const includeAllForArchivedCategory = archivedCategoryLookup[categoryId];
        const isArchivedType = type.isActive === false;
        if (!includeAllForArchivedCategory && !isArchivedType) return acc;
        acc[categoryId] = acc[categoryId] ? [...acc[categoryId], type] : [type];
        return acc;
      }, {});

      const categoriesWithArchivedTypes = allCategories.filter(
        (category) => archivedTypesByCategory[category.id]?.length
      );
      const archivedCategories = allCategories.filter((category) => category.isActive === false);
      const categoriesToShow = [...new Set([...categoriesWithArchivedTypes, ...archivedCategories])];

      setCategories(categoriesToShow);
      setTypesByCategory(archivedTypesByCategory);
    } catch (error) {
      toast.error("Failed to load archived materials");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedTypes = async (categoryId) => {
    if (!categoryId) return;
    setTypesLoading((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const response = await MaterialService.getTypes(categoryId);
      const category = categories.find((cat) => cat.id === categoryId);
      const includeAllForArchivedCategory = category?.isActive === false;
      setTypesByCategory((prev) => ({
        ...prev,
        [categoryId]: includeAllForArchivedCategory
          ? response.data || []
          : response.data.filter((type) => type.isActive === false) || [],
      }));
      // After refreshing types, refresh categories list so active categories disappear if no archived types remain
      getArchived();
    } catch (error) {
      toast.error("Failed to load archived material types");
    } finally {
      setTypesLoading((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  const handleToggleCategory = (categoryId) => {
    const isOpening = !expandedCategoryIds[categoryId];
    setExpandedCategoryIds((prev) => ({ ...prev, [categoryId]: isOpening }));
    if (isOpening && !typesByCategory[categoryId]) {
      fetchArchivedTypes(categoryId);
    }
  };

  const handleRestoreCategory = async (id) => {
    try {
      await MaterialService.update(id, { isActive: true });

      // Restore all archived types under this category as well
      const typesResponse = await MaterialService.getTypes(id);
      const archivedTypes = (typesResponse.data || []).filter((type) => type.isActive === false);
      if (archivedTypes.length) {
        await Promise.all(archivedTypes.map((type) => MaterialService.updateType(type.id, { isActive: true })));
      }

      toast.success(archivedTypes.length ? "Material category and its types restored" : "Material category restored");
      getArchived();
    } catch (error) {
      toast.error("Failed to restore material category");
    }
  };

  const handleRestoreType = async (categoryId, typeId) => {
    try {
      await MaterialService.updateType(typeId, { isActive: true });
      toast.success("Material type restored");
      fetchArchivedTypes(categoryId);
    } catch (error) {
      toast.error("Failed to restore material type");
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-3">
      {isLoading && <p className="text-[12px] font-OpenSansRegular text-subTitleColor">Loading archived materials…</p>}
      {!isLoading && categories.length === 0 && (
        <p className="text-[12px] font-OpenSansRegular text-subTitleColor">No archived materials.</p>
      )}
      {!isLoading &&
        categories.map((category) => {
          const isArchived = !category?.isActive;
          const isOpen = expandedCategoryIds[category.id];
          const types = typesByCategory[category.id] || [];
          const isTypesLoading = typesLoading[category.id];
          return (
            <div key={category.id || category.name} className="rounded-[4px] border border-borderColor bg-white">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div
                  className="flex w-full cursor-pointer items-center justify-between text-left"
                  onClick={() => handleToggleCategory(category.id)}
                >
                  <div>
                    <p className="text-[12px] font-OpenSansRegular font-[600] uppercase text-titleColor">
                      {category?.name}
                    </p>
                    <p className="text-[12px] font-OpenSansRegular text-subTitleColor">
                      Unit: {category?.unit || "N/A"} · Mandatory: {category?.isMandatory ? "Yes" : "No"}
                    </p>
                  </div>
                  <svg
                    className={`h-4 w-4 text-titleColor transition-transform duration-150 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isArchived && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestoreCategory(category.id);
                    }}
                    className="rounded-[6px] bg-blueColor px-3 py-1 text-[12px] font-OpenSansMedium text-white"
                  >
                    Restore
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="border-t border-borderColor px-4 py-3">
                  <div className="mb-2 text-[12px] font-OpenSansRegular text-subTitleColor">
                    Archived material types
                  </div>
                  <div className="flex flex-col divide-y divide-borderColor overflow-visible rounded-[4px] border border-borderColor">
                    {isTypesLoading && (
                      <p className="px-3 py-2 text-[12px] font-OpenSansRegular text-subTitleColor">Loading types…</p>
                    )}
                    {!isTypesLoading && types.length === 0 && (
                      <p className="px-3 py-2 text-[12px] font-OpenSansRegular text-subTitleColor">
                        No archived material types.
                      </p>
                    )}
                    {!isTypesLoading &&
                      types.map((type) => (
                        <div
                          key={type.id || type.name}
                          className="flex items-center justify-between px-3 py-2 text-[12px] font-OpenSansRegular text-titleColor"
                        >
                          <span>{type.name}</span>
                          {!isArchived && (
                            <button
                              type="button"
                              onClick={() => handleRestoreType(category.id, type.id)}
                              className="rounded-[6px] bg-blueColor px-2 py-1 text-[11px] font-OpenSansMedium text-white"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default Archive;
